import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { RESUME_V2_FIELD } from '@glass/web/modules/resumes/fieldKeys';

import useContentSeed from '@glass/shared/modules/content/useContentSeed';
import usePageData from '@glass/shared/modules/pageData/usePageData';
import contentResumesQuery from '@glass/shared/modules/resumes/queries/contentResumes.client.graphql';
import randomResumeV2Query from '@glass/shared/modules/resumes/queries/randomResumeV2.graphql';

const usePlaceholderResume = ({ skip } = {}) => {
  const { isFallback } = useRouter();
  const { iSlug, jSlug, seed: seedOverride } = usePageData();

  const seed = useContentSeed({ seedOverride, iSlug, jSlug });

  const {
    data: dataRandom,
    loading: loadingRandom,
    error: errorRandom,
  } = useQuery(randomResumeV2Query, {
    variables: {
      seed,
      iSlug,
      jSlug,
    },
    skip: skip || isFallback || !seed,
  });
  const {
    data: dataFallback,
    loading: loadingFallback,
    error: errorFallback,
  } = useQuery(contentResumesQuery, {
    variables: {
      where: {
        slug: 'default',
      },
      first: 1,
    },
    skip: skip || isFallback || (!!seed && !errorRandom),
  });

  const fallbackResumeV2 = dataFallback?.contentResumes?.[0]?.[RESUME_V2_FIELD];
  const placeholderResume = useMemo(() => {
    if (dataRandom?.randomResumeV2) {
      return dataRandom.randomResumeV2;
    }
    if (fallbackResumeV2) {
      return {
        [RESUME_V2_FIELD]: fallbackResumeV2,
      };
    }
    return null;
  }, [dataRandom, fallbackResumeV2]);

  return {
    placeholderResume,
    loading: isFallback || loadingFallback || loadingRandom,
    error: errorFallback || errorRandom,
  };
};

export default usePlaceholderResume;
